import CameraAltOutlinedIcon from '@material-ui/icons/CameraAltOutlined';
import LinkRoundedIcon from '@material-ui/icons/LinkRounded';
import MicNoneRoundedIcon from '@material-ui/icons/MicNoneRounded';
import PhotoLibraryOutlinedIcon from '@material-ui/icons/PhotoLibraryOutlined';
import SendRoundedIcon from '@material-ui/icons/SendRounded';
import StopRoundedIcon from '@material-ui/icons/StopRounded';
import { Form as FormikForm } from 'formik';
import React from 'react';
import styled, { css } from 'styled-components/macro';
import * as common from '../../common/elements/Form';
import {
  focusRing,
  fontSize,
  layout,
  lineHeight,
  media,
  neutral,
  radius,
  shadow,
  space,
  transition,
} from '../../../theme/tokens';

// The form is a fixed 8rem tall: the chat list and file preview are offset by
// the same amount, so keep these in step.
const formHeight = '8rem';
const linkInputHeight = '6rem';

const iconButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${layout.touchTarget};
  min-height: ${layout.touchTarget};
  padding: 0;
  border: none;
  border-radius: ${radius.pill};
  background: none;
  color: ${({ theme }) => theme.textDark};
  cursor: pointer;
  transition: background ${transition.fast}, color ${transition.fast};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.hoverFill};
  }
  &:disabled {
    cursor: default;
  }
  ${focusRing}
`;

export const Form = styled(FormikForm)`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  grid-template-areas: 'camera text mic send';
  align-items: center;
  justify-items: center;
  column-gap: ${space[1]};
  height: ${formHeight};
  width: 100%;
  margin: 0;
  padding: 0 ${space[2]};
  border-top: 1px solid ${neutral.border};
  box-shadow: ${shadow.up};
  position: fixed;
  bottom: 0;
  background: ${({ theme }) => theme.appBackground};

  ${media.md`
    column-gap: ${space[3]};
    padding: 0 ${space[5]};
  `}

  ${({ $isStaff }) =>
    $isStaff
      ? css`
          position: relative;
          ${media.md`
            /* the desktop staff panel is narrow (25vw), so give the
               text box the room and let the icon buttons hug their icons */
            column-gap: ${space[1]};
            padding: 0 ${space[2]};
            & > button {
              min-width: 3.2rem;
            }
          `}
        `
      : css`
          ${media.md`
            width: calc(100vw - ${layout.sidebarWidth});
          `}
        `}

  ${({ $renderLinkInput }) =>
    $renderLinkInput &&
    css`
      grid-template-columns: auto minmax(0, 1fr) auto auto auto;
      grid-template-areas: 'camera text mic link send';
    `}
`;

export const LockedForm = styled(Form).attrs({ as: 'section' })`
  grid-template-columns: 1fr;
  color: ${({ theme }) => theme.textDark};
`;

export const MessageSubmitButton = styled.button`
  ${iconButton}
  grid-area: send;
  color: ${({ theme }) => theme.userMessage};
  &:disabled {
    color: ${({ theme }) => theme.textDark};
  }
`;

export const MessageInput = styled(common.FieldInput)`
  grid-area: text;
  width: 100%;
  font-size: ${fontSize.md};
`;

export const CameraLabel = styled.button`
  ${iconButton}
  grid-area: camera;
  ${({ fileSelected }) =>
    fileSelected &&
    css`
      color: ${({ theme }) => theme.userMessage};
    `}
`;

export const AttachMenuContainer = styled.section`
  display: flex;
  flex-direction: column;
  padding: ${space[2]} 0;
`;

export const AttachMenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${space[3]};
  width: 100%;
  min-height: ${layout.touchTarget};
  border: none;
  background: none;
  padding: ${space[3]} ${space[4]};
  font-family: inherit;
  font-size: ${fontSize.md};
  text-align: left;
  color: ${({ theme }) => theme.textDark};
  &:hover {
    cursor: pointer;
    background: ${({ theme }) => theme.hoverFill};
  }
  ${focusRing}
`;

export const RecordButton = styled.button`
  ${iconButton}
  grid-area: mic;
  ${({ fileSelected }) =>
    fileSelected &&
    css`
      color: ${({ theme }) => theme.userMessage};
    `}
  ${({ isRecording }) =>
    isRecording &&
    css`
      color: ${({ theme }) => theme.buttonWarning};
    `}
`;

export const HiddenImageInput = styled.input`
  display: none;
`;

export const CameraIcon = () => (
  <CameraAltOutlinedIcon style={{ fontSize: '3rem' }} />
);

export const LibraryIcon = () => (
  <PhotoLibraryOutlinedIcon style={{ fontSize: '2.2rem' }} />
);

export const MicIcon = () => (
  <MicNoneRoundedIcon style={{ fontSize: '3rem' }} />
);

export const StopIcon = () => <StopRoundedIcon style={{ fontSize: '3rem' }} />;

export const LinkIcon = () => <LinkRoundedIcon style={{ fontSize: '3rem' }} />;

export const AttachLinkButton = styled.button`
  ${iconButton}
  grid-area: link;
`;

export const SendIcon = () => (
  <SendRoundedIcon style={{ fontSize: '3.2rem' }} />
);

export const MessagePreview = styled.div`
  position: fixed;
  bottom: ${formHeight};
  width: 100vw;
  border-radius: ${radius.lg} ${radius.lg} 0 0;
  background: ${({ theme }) => theme.appBackground};
  border-top: 1px solid ${neutral.border};
  box-shadow: ${shadow.up};
  ${({ inLine }) =>
    inLine &&
    css`
      position: absolute;
      right: 0;
    `};

  ${media.md`
    width: calc(100vw - ${layout.sidebarWidth});
    margin-right: ${space[4]};
  `}

  ${({ isStaff }) =>
    isStaff &&
    css`
      ${media.md`
        /* matches the staff chat panel */
        width: 25vw;
        min-width: 325px;
        margin-right: 0;
      `}
    `}

  ${({ isLinkInputVisible }) =>
    isLinkInputVisible &&
    css`
      bottom: calc(${formHeight} + ${linkInputHeight});
    `}
`;

export const LinkInputContainer = styled(MessagePreview)`
  height: ${linkInputHeight};
  display: flex;
  align-items: center;
  gap: ${space[2]};
  padding: 0 ${space[4]} 0 ${space[2]};
`;

export const LinkInput = styled(common.FieldInput)`
  flex: 1;
  min-width: 0;
  font-size: ${fontSize.md};
`;

export const MessageContainer = styled.div`
  width: fit-content;
  max-width: 100%;
  margin-left: auto;
  margin-right: ${space[2]};
  ${media.md`
    margin-right: ${space[7]};
  `}
`;

export const CloseErrorButton = styled.button`
  ${iconButton}
`;

export const MessageTextArea = styled(common.TextArea)`
  grid-area: text;
  width: 100%;
  /* tight line height and small padding keep 3 rows inside the 8rem form */
  padding: ${space[2]} ${space[3]};
  border: 1px solid ${neutral.border};
  border-radius: ${radius.lg};
  background: ${neutral.canvas};
  line-height: ${lineHeight.tight};
  resize: none;
  transition: border-color ${transition.fast}, background ${transition.fast};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.userMessage};
    background: ${neutral.surface};
  }
`;

export * from '../../common/elements/Form';
export * from '../../common/elements/Students';
